import type {Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'
import {
  type CompletedReview,
  type PendingReviewRequest,
  type ReviewRequestsResponse,
  ReviewState,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {reviewState} from './review-state-helpers'

export interface TeamCompositeState {
  teamName: string
  teamReviewer: Reviewer | null
  compositeState: ReviewState
  stateLabel: string
  completedUserReviews: CompletedReview[]
  pendingUserRequests: PendingReviewRequest[]
  isCodeOwner: boolean
}

/**
 * Groups review activity by team and computes a single composite review state for each.
 *
 * Processes two data sources:
 * 1. Pending review requests — either directly assigned to a team, or delegated from a team to individual users.
 * 2. Completed reviews — submitted by users on behalf of teams.
 *
 * The composite state reflects the most significant review across all team members
 * (changes_requested > approved > commented > pending).
 */
export function computeTeamCompositeStates(data: ReviewRequestsResponse): TeamCompositeState[] {
  const teamReviewerMap = new Map<string, Reviewer>()
  const teamStates = new Map<string, ReviewState[]>()
  const teamCompletedReviews = new Map<string, CompletedReview[]>()
  const teamPendingRequests = new Map<string, PendingReviewRequest[]>()
  const teamCodeOwners = new Set<string>()

  for (const req of data.pendingReviewRequests ?? []) {
    const reviewer = req.reviewer
    if (reviewer?.reviewerType === 'Team') {
      // Team itself is directly requested — mark team state as pending.
      // No entry in teamPendingRequests because there is no delegated user yet.
      teamReviewerMap.set(reviewer.displayLogin, reviewer)
      const states = teamStates.get(reviewer.displayLogin) ?? []
      states.push(ReviewState.PENDING)
      teamStates.set(reviewer.displayLogin, states)
      if (req.isCodeOwner) teamCodeOwners.add(reviewer.displayLogin)
    } else if (req.delegatedFromTeam) {
      // An individual user was delegated from a team — track as a pending user request
      // so the UI can display them nested under the team row.
      const teamName = req.delegatedFromTeam.displayLogin
      teamReviewerMap.set(teamName, req.delegatedFromTeam)
      const pending = teamPendingRequests.get(teamName) ?? []
      pending.push(req)
      teamPendingRequests.set(teamName, pending)
      if (req.isCodeOwner && req.isCodeOwnerFromDelegation) teamCodeOwners.add(teamName)
    }
  }

  // Collect completed reviews and group them by the team(s) the reviewer acted on behalf of.
  for (const review of data.completedReviews ?? []) {
    for (const team of review.onBehalfOfTeams) {
      const teamName = team.displayLogin
      teamReviewerMap.set(teamName, team)
      const states = teamStates.get(teamName) ?? []
      states.push(review.state)
      teamStates.set(teamName, states)

      const reviews = teamCompletedReviews.get(teamName) ?? []
      reviews.push(review)
      teamCompletedReviews.set(teamName, reviews)
    }
  }

  const results: TeamCompositeState[] = []
  for (const [teamName, states] of teamStates) {
    const compositeState = resolveCompositeState(states)
    const {label} = reviewState(compositeState)
    results.push({
      teamName,
      teamReviewer: teamReviewerMap.get(teamName) ?? null,
      compositeState,
      stateLabel: label,
      completedUserReviews: teamCompletedReviews.get(teamName) ?? [],
      pendingUserRequests: teamPendingRequests.get(teamName) ?? [],
      isCodeOwner: teamCodeOwners.has(teamName),
    })
  }

  return results
}

function resolveCompositeState(states: ReviewState[]): ReviewState {
  if (states.length === 0) return ReviewState.PENDING

  // If anyone requested changes, team is blocked
  if (states.includes(ReviewState.CHANGES_REQUESTED)) return ReviewState.CHANGES_REQUESTED
  // If anyone approved, team is approved (even if others only commented)
  if (states.includes(ReviewState.APPROVED)) return ReviewState.APPROVED
  // If only comments, show commented
  if (states.includes(ReviewState.COMMENTED)) return ReviewState.COMMENTED
  // Otherwise pending
  return ReviewState.PENDING
}
